import { Cushion, FlexContainer, Typography } from '@pitchero/react-ui';
import { find } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

const EventSummaries = ({ sportId, events, isMobile }) => {
  const renderFootballHockeySummaries = () => {
    const redCardEvents = {};
    const allOtherEvents = {};
    events.forEach((player) => {
      player.events
        .filter((event) => !event.toLowerCase().includes('yellow card'))
        .forEach((event) => {
          if (
            event.toLowerCase().includes('sent off') ||
            event.toLowerCase().includes('red card')
          ) {
            redCardEvents[player.name] = redCardEvents[player]
              ? [...redCardEvents[player.name], event]
              : [event];
          } else {
            allOtherEvents[player.name] = allOtherEvents[player.name]
              ? [...allOtherEvents[player.name], event]
              : [event];
          }
        });
    });

    const renderPlayerEvents = (playerEvents) =>
      Object.entries(playerEvents).map(([name, eventList], i) => (
        <Cushion key={name} right="xxsmall" style={{ display: 'inline-block' }} component="span">
          {`${name} (${eventList.join('), (')})`}
          {i !== Object.keys(playerEvents).length - 1 ? ', ' : ''}
        </Cushion>
      ));

    return (
      <Typography
        component="div"
        preset="description"
        size={isMobile ? 12 : 14}
        color="#fff"
        lineHeight="18px"
      >
        {renderPlayerEvents(allOtherEvents)}
        {renderPlayerEvents(redCardEvents)}
      </Typography>
    );
  };

  // We want rugby summary events to be follow a pre-defined order, so tries are always first,
  // then conversions... etc. The comparator is used to by the array sort method
  const rugbyComparator = (a, b) => {
    const order = ['tries', 'conversions', 'penalties', 'drop goals', 'sent off'];
    const aIndex = order.indexOf(a?.toLowerCase());
    const bIndex = order.indexOf(b?.toLowerCase());

    // If both elements are in the predefined order, sort based on their indices
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only one element is in the predefined order, prioritize it
    if (aIndex !== -1) {
      return -1;
    }
    if (bIndex !== -1) {
      return 1;
    }

    // If none of the elements are in the predefined order, keep their original order
    return 0;
  };

  const renderRugbySummaries = () => {
    const eventTypeLabels = events.map((eventType) => eventType.label).sort(rugbyComparator);
    return (
      <FlexContainer flexDirection="column" style={{ gap: '5px' }}>
        {eventTypeLabels.map((eventTypeLabel) => {
          const eventsOfType = find(events, { label: eventTypeLabel });
          const players = eventsOfType ? eventsOfType.players : [];
          if (players.length === 0) {
            return null;
          }

          return (
            <React.Fragment key={eventTypeLabel}>
              <Typography
                preset="description"
                color="#fff"
                size={isMobile ? 12 : 14}
                lineHeight="18px"
              >
                <span style={{ fontWeight: 'bold' }}>
                  {eventTypeLabel}
                  {': '}
                </span>
                {players.map((player, i) => (
                  <Cushion
                    key={player.id}
                    right="xxsmall"
                    style={{ display: 'inline-block' }}
                    component="span"
                  >
                    {`${player.name}${player.count > 1 ? ` (${player.count})` : ''}`}
                    {i !== players.length - 1 ? ', ' : ''}
                  </Cushion>
                ))}
              </Typography>
            </React.Fragment>
          );
        })}
      </FlexContainer>
    );
  };

  const renderOtherSportSummaries = () => (
    <Typography
      size={isMobile ? 12 : 14}
      component="div"
      preset="description"
      color="#fff"
      lineHeight="18px"
    >
      {events.map((player, i) => (
        <Cushion
          key={player.id}
          right="xxsmall"
          style={{ display: 'inline-block' }}
          component="span"
        >
          {`${player.name} (${player.total})`}
          {i !== events.length - 1 ? ', ' : ''}
        </Cushion>
      ))}
    </Typography>
  );

  if (events.length === 0) return null;

  switch (sportId) {
    case 1:
    case 7:
      return renderFootballHockeySummaries();
    case 2:
    case 3:
      return renderRugbySummaries();
    default:
      return renderOtherSportSummaries();
  }
};

EventSummaries.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  sportId: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default EventSummaries;
